import React, { useEffect, useState, useRef } from "react";
import { IFiltersProps } from "./filters.props";
import FaIcon from "../fa-icon/fa-icon";
import IconEnum from "../../enums/icon.enum";
import SizeEnum from "../../enums/size.enum";
import "./filters.scss";

const Filters = (props: IFiltersProps) => {
  const {
    filters,
    updateFiltersCallback,
    onReset,
    sliderPosition = { left: 0, top: "0px", width: "100%", height: "100%" },
    className = "",
    mpngStyle = false
  } = props;
  const [showFilters, setShowFilters] = useState(false);
  const [showCategories, setShowCategories] = useState({
    ...filters.reduce((acc, filter) => ({ ...acc, [filter.name]: true }), { "Active Filters": true })
  });
  const [showBackground, setShowBackground] = useState(false);
  const [backgroundOpacity, setBackgroundOpacity] = useState(0);
  const [leftOffset, setLeftOffset] = useState(`${-330 + sliderPosition.left}px`); // Default hidden left offset for the slider
  const [showActiveFilters, setShowActiveFilters] = useState(false);
  const activeFiltersRef = useRef(null);
  const filtersContainerRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    // Close/open the filter slider
    let openTimeoutId = null;
    let closeTimeoutId = null;
    if (showFilters) {
      setLeftOffset(`${sliderPosition.left}px`);
      setShowBackground(true);
      openTimeoutId = setTimeout(() => {
        setBackgroundOpacity(1);
      }, 10);
    } else {
      setLeftOffset(`${-330 + sliderPosition.left}px`);
      setBackgroundOpacity(0);
      closeTimeoutId = setTimeout(() => {
        setShowBackground(false);
      }, 800);
    }

    return () => {
      clearTimeout(openTimeoutId);
      clearTimeout(closeTimeoutId);
    };
  }, [showFilters, setShowFilters, sliderPosition.left]);

  const updateFilters = (category: string, toggledValues: string[], clearAll: boolean = false) => {
    const updatedFilters = filters.map((filterCategory) => {
      if (filterCategory.name === category) {
        return {
          ...filterCategory,
          values: filterCategory.values.map((currentValue) => {
            if (clearAll) {
              return { ...currentValue, checked: false };
            } else if (toggledValues.some((toggledValue) => toggledValue === currentValue.name)) {
              return { ...currentValue, checked: !currentValue.checked };
            }
            return currentValue;
          })
        };
      }
      return filterCategory;
    });
    updateFiltersCallback(updatedFilters);
  };

  const activeFilterCount = () => {
    return filters.reduce((count, filterCategory) => {
      return (
        count +
        filterCategory.values.reduce((innerCount, value) => {
          return innerCount + (value.checked ? 1 : 0);
        }, 0)
      );
    }, 0);
  };

  const toggleCategory = (category: string) => {
    setShowCategories((prev) => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  useEffect(() => {
    const handleFocusOutside = (event: MouseEvent | FocusEvent) => {
      if (activeFiltersRef.current && !activeFiltersRef.current.contains(event.target as Node)) {
        setShowActiveFilters(false);
      }
    };

    if (showActiveFilters) {
      document.addEventListener("mousedown", handleFocusOutside);
      document.addEventListener("focusin", handleFocusOutside); // for keyboard navigation
    } else {
      document.removeEventListener("mousedown", handleFocusOutside);
      document.removeEventListener("focusin", handleFocusOutside); // for keyboard navigation
    }

    return () => {
      document.removeEventListener("mousedown", handleFocusOutside);
      document.removeEventListener("focusin", handleFocusOutside); // for keyboard navigation
    };
  }, [showActiveFilters, activeFiltersRef]);

  useEffect(() => {
    if (mpngStyle && filtersContainerRef.current) {
      filtersContainerRef.current.style.setProperty(
        "--filters-primary-color",
        "var(--mps-component-library-mpng-blue)"
      );
      filtersContainerRef.current.style.setProperty(
        "--filters-accent-color",
        "var(--mps-component-library-mpng-accent-blue)"
      );
      filtersContainerRef.current.style.setProperty(
        "--filters-light-color",
        "var(--mps-component-library-mpng-light-blue)"
      );
    }
  }, [mpngStyle]);

  return (
    <div ref={filtersContainerRef} className={`filters-container ${className}`} data-testid="filters-container">
      <div className="filters-buttons-container">
        <button
          className={`filters-button ${activeFilterCount() > 0 && "has-active-filters"}`}
          onClick={() => {
            setShowFilters(true);
            closeButtonRef.current?.focus();
          }}
          data-testid="filters-button"
        >
          <FaIcon className="filters-icon" icon={IconEnum.FILTER} />
          <span>Filters</span>
        </button>
        {activeFilterCount() > 0 && (
          <div ref={activeFiltersRef}>
            <button
              className="active-filters-button"
              onMouseEnter={() => {
                setShowActiveFilters(true);
              }}
              onClick={() => {
                setShowActiveFilters((prev) => !prev);
              }}
              data-testid="active-filters-button"
            >
              <span className="filter-count">{activeFilterCount()}</span>
            </button>
            {showActiveFilters && (
              <div className="active-filters-dropdown" data-testid="active-filters-dropdown">
                {filters.map((filterCategory) => {
                  return filterCategory.values.map((value) => {
                    return value.checked ? (
                      <div
                        className="active-filters-dropdown-item"
                        key={value.name}
                        data-testid={`active-filter-${filterCategory.name}-${value.name}`}
                      >
                        {`${filterCategory.name}: ${value.name}`}
                        <button
                          onClick={() => {
                            updateFilters(filterCategory.name, [value.name]);
                            setShowActiveFilters(false);
                          }}
                          className="filters-icon-button"
                          data-testid={`remove-active-filter-${filterCategory.name}-${value.name}`}
                          aria-label={`Remove active filter ${filterCategory.name}: ${value.name}`}
                        >
                          <FaIcon size={SizeEnum.MEDIUM} squared={true} icon={IconEnum.XMARK} />
                        </button>
                      </div>
                    ) : null;
                  });
                })}
              </div>
            )}
          </div>
        )}
      </div>
      <button
        className="filter-slider-background"
        aria-label="Close Filters"
        data-testid="filter-slider-background"
        style={{
          display: showBackground ? "flex" : "none",
          opacity: backgroundOpacity,
          width: sliderPosition.width,
          height: sliderPosition.height,
          left: sliderPosition.left,
          top: sliderPosition.top
        }}
        onClick={() => {
          setShowFilters(false);
        }}
      />
      <div
        className="filter-slider"
        data-testid="filter-slider"
        style={{
          left: leftOffset,
          top: sliderPosition.top,
          height: sliderPosition.height
        }}
      >
        <div className="filter-slider-header">
          <h3 className="filter-slider-title">Filters</h3>
          <button
            ref={closeButtonRef}
            onClick={() => {
              setShowFilters(false);
            }}
            className="filters-icon-button"
            data-testid="filters-close-button"
            aria-label="Close Filters"
          >
            <FaIcon size={SizeEnum.MEDIUM} squared={true} icon={IconEnum.XMARK} />
          </button>
        </div>
        {activeFilterCount() > 0 && (
          <button
            onClick={() => {
              onReset();
            }}
            className="reset-filters-button"
            data-testid="reset-filters-button"
          >
            <FaIcon className="reset-filters-icon" icon={IconEnum.ROTATE_RIGHT} />
            <span>Reset Filters</span>
          </button>
        )}
        <hr />
        <div className="active-filters">
          <button
            className="active-filters-header"
            onClick={() => {
              toggleCategory("Active Filters");
            }}
            data-testid="active-filters-header"
          >
            <span>Active Filters</span>
            <FaIcon icon={showCategories["Active Filters"] ? IconEnum.SORT_UP : IconEnum.SORT_DOWN} />
          </button>
          <div
            className="active-filters-container"
            style={{ display: showCategories["Active Filters"] ? "flex" : "none" }}
            data-testid="active-filters-container"
          >
            {activeFilterCount() === 0 ? (
              <span className="active-filter">No Active Filters</span>
            ) : (
              <>
                {filters.map((filterCategory) => {
                  const activeFilters = filterCategory.values.filter((value) => value.checked);
                  return activeFilters.length > 0 ? (
                    <div className="active-filter" key={filterCategory.name}>
                      {activeFilters.length > 1 ? (
                        <span
                          className="active-filter-count-container"
                          data-testid={`active-filter-count-${filterCategory.name}`}
                        >
                          {`${filterCategory.name}: `}
                          <span className="active-filter-count">{`${activeFilters.length}`}</span>
                        </span>
                      ) : (
                        <span className="active-filter-name" data-testid={`active-filter-name-${filterCategory.name}`}>
                          {`${filterCategory.name}: ${activeFilters[0].name}`}
                        </span>
                      )}
                      <button
                        onClick={() => {
                          updateFilters(
                            filterCategory.name,
                            activeFilters.map((value) => value.name)
                          );
                        }}
                        className="filters-icon-button"
                        data-testid={`remove-active-filters-${filterCategory.name}`}
                        aria-label={`Remove active filters for ${filterCategory.name}`}
                      >
                        <FaIcon size={SizeEnum.MEDIUM} squared={true} icon={IconEnum.XMARK} />
                      </button>
                    </div>
                  ) : null;
                })}
              </>
            )}
          </div>
        </div>
        <hr />
        {filters.map((filterCategory) => {
          return (
            <div key={filterCategory.name} className="filter-category">
              <button
                onClick={() => {
                  toggleCategory(filterCategory.name);
                }}
                className="filter-category-header"
                data-testid={`filter-category-header-${filterCategory.name}`}
              >
                <span>{filterCategory.name}</span>
                <FaIcon icon={showCategories[filterCategory.name] ? IconEnum.SORT_UP : IconEnum.SORT_DOWN} />
              </button>
              <div
                className="filter-category-values"
                style={{ display: showCategories[filterCategory.name] ? "flex" : "none" }}
                data-testid={`filter-category-values-${filterCategory.name}`}
              >
                {filterCategory.values.map((value) => {
                  return (
                    <label className="filter-category-value" key={value.name}>
                      <input
                        className="visually-hidden-filter-input"
                        type="checkbox"
                        onChange={() => {
                          updateFilters(filterCategory.name, [value.name]);
                        }}
                        checked={value.checked}
                      />
                      <div className={`visible-filter-input ${value.checked && "checked"}`} aria-hidden="true">
                        {value.checked && <FaIcon icon={IconEnum.CHECK} className="filter-checkbox-icon" />}
                      </div>
                      {value.name}
                    </label>
                  );
                })}
              </div>
              <hr />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Filters;
